import { DateRangePicker, DateRangePickerTypes, Text, HStack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import React, { useEffect, useMemo, useState } from 'react';

import { FilterModalTypes } from '../..';

export declare namespace DateRangeTypes {
  type SelectionProps = {
    initialDate: Date | null;
    finalDate: Date | null;
  };

  type ValueChange = (dates: SelectionProps, fieldName: string, filterIndex: number) => void;

  interface Props {
    type: 'dateRange';
    fieldName: string;
    title: string;
    placeholder: string;
    filterIndex: number;
    onValueChange: ValueChange;
    onValidationChange: (isValid: boolean) => void;
    selectedFilters: FilterModalTypes.SelectedFiltersProps[];
    selection?: SelectionProps;
    zIndex: number;
    calendarAvailableRange?: DateRangePickerTypes.Props['availableRange'];
    isRequired?: boolean;
    showOnlyIf: (selectedFilters: FilterModalTypes.SelectedFiltersProps[]) => boolean;
    maxDaysRange?: number;
  }
}

export const hasDateRangeSelection = (dates: DateRangeTypes.SelectionProps) =>
  !!dates.initialDate || !!dates.finalDate;

export const DateRange: React.FC<DateRangeTypes.Props> = ({
  title,
  fieldName,
  filterIndex,
  onValueChange,
  onValidationChange,
  selection,
  placeholder,
  zIndex,
  calendarAvailableRange,
  showOnlyIf,
  selectedFilters,
  maxDaysRange,
  isRequired,
}) => {
  const [hasDateRange, setHasDateRange] = useState<boolean>(
    !!selection?.initialDate && !!selection?.finalDate
  );

  const shouldShow = useMemo(
    () => (showOnlyIf ? showOnlyIf(selectedFilters) : true),
    [showOnlyIf, selectedFilters]
  );

  const handleClearDates = () => {
    onValueChange({ initialDate: null, finalDate: null }, fieldName, filterIndex);
    setHasDateRange(false);
  };

  const handleSelectDates = (initialDate: Date, finalDate: Date) => {
    if (!initialDate || !finalDate) {
      setHasDateRange(false);
      onValueChange({ initialDate, finalDate }, fieldName, filterIndex);
      return;
    }

    const initialDateObj = new Date(initialDate);
    const finalDateObj = new Date(finalDate);

    const differenceInTime = finalDateObj.getTime() - initialDateObj.getTime();
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

    if (maxDaysRange && differenceInDays > maxDaysRange) {
      const adjustedFinalDate = new Date(initialDateObj);
      adjustedFinalDate.setDate(initialDateObj.getDate() + maxDaysRange);

      onValueChange({ initialDate, finalDate: adjustedFinalDate }, fieldName, filterIndex);
    } else {
      onValueChange({ initialDate, finalDate }, fieldName, filterIndex);
    }

    setHasDateRange(true);
  };

  useEffect(() => {
    if (isRequired && shouldShow) {
      onValidationChange(hasDateRange);
    } else {
      onValidationChange(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDateRange, isRequired, shouldShow]);

  if (!shouldShow) {
    if (selection?.finalDate || selection?.initialDate) {
      setHasDateRange(false);
      onValueChange({ initialDate: null, finalDate: null }, fieldName, filterIndex);
    }

    return null;
  }

  return (
    <VStack w="full" space={Spacing['inline.nano']} zIndex={2 + zIndex}>
      {!!title && (
        <Text variant="semibold" fontSize={Typography['fontsize.md']} lineHeight="distant">
          {title}
        </Text>
      )}
      <HStack w="full" zIndex={2 + zIndex}>
        <DateRangePicker
          initialDate={selection?.initialDate || undefined}
          finalDate={selection?.finalDate || undefined}
          onClear={handleClearDates}
          onSelectDates={handleSelectDates}
          calendarPosition="left"
          placeholder={placeholder}
          availableRange={calendarAvailableRange}
        />
      </HStack>
      {isRequired && !hasDateRange && (
        <Text variant="semibold" fontSize="sm" color="feedback.error.default">
          Intervalo de data é obrigatório
        </Text>
      )}
    </VStack>
  );
};
